import type { ProductMetadata } from "../../../../types/productMetadata";
import {
  METADATA_FIELD_CONFIGS,
  METADATA_TAG_CONFIGS,
  METADATA_TEXT_CONFIGS,
} from "../../../../types/productMetadata";
import { escapeCSVCell, sanitizeCSVText } from "../../../../utils/csvExport";
import type { AdminProductItem } from "../hooks/useAdminProductsList";

const ID_HEADERS = ["product_type", "product_id", "provider_id", "title"] as const;
const FIELD_KEYS = METADATA_FIELD_CONFIGS.map((c) => c.key);
const ALL_HEADERS = [...ID_HEADERS, ...FIELD_KEYS];

const OPTIONS_MAP = new Map(METADATA_TAG_CONFIGS.map((c) => [c.key, c.options]));
const TEXT_KEYS = new Set<keyof ProductMetadata>(METADATA_TEXT_CONFIGS.map((c) => c.key));

function normalizeToCanonical(value: string, options: readonly string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  for (const opt of options) {
    if (opt.toLowerCase() === lower) return opt;
  }
  return null;
}

function escapeSpreadsheetTextCell(value: string): string {
  const reversibleValue = value.startsWith("'") ? `'${value}` : value;
  return escapeCSVCell(sanitizeCSVText(reversibleValue));
}

function restoreSpreadsheetText(value: string): string {
  if (value.startsWith("''") || /^'[=+\-@\t\r]/.test(value)) {
    return value.slice(1);
  }
  return value;
}

export function generateMetadataCsv(
  products: AdminProductItem[],
  getMetadata: (product: AdminProductItem) => ProductMetadata | null
): string {
  const rows: string[] = [];
  rows.push(ALL_HEADERS.map(escapeCSVCell).join(","));

  for (const product of products) {
    const metadata = getMetadata(product) ?? {};
    const cells: string[] = [
      product.productType,
      String(product.id),
      product.providerId === null ? "" : String(product.providerId),
      escapeSpreadsheetTextCell(product.title),
      ...FIELD_KEYS.map((key) => {
        const value = metadata[key] ?? "";
        return TEXT_KEYS.has(key)
          ? escapeSpreadsheetTextCell(value)
          : escapeCSVCell(value);
      }),
    ];
    rows.push(cells.join(","));
  }

  return rows.join("\n");
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export interface CsvRow {
  product_type: AdminProductItem["productType"];
  product_id: string;
  provider_id: string | null;
  title: string;
  tags: Partial<ProductMetadata>;
}

export interface CsvValidationError {
  row: number;
  column: string;
  value: string;
  message: string;
}

export interface CsvParseResult {
  rows: CsvRow[];
  errors: CsvValidationError[];
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        cells.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  cells.push(current.trim());
  return cells;
}

export function parseCsvFile(
  text: string,
  products: AdminProductItem[]
): CsvParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { rows: [], errors: [{ row: 0, column: "", value: "", message: "CSV has no data rows" }] };
  }

  const headerLine = parseCsvLine(lines[0]);
  const headerMap = new Map(headerLine.map((h, i) => [h.toLowerCase(), i]));

  const missingHeaders = ALL_HEADERS.filter(
    (h) => !headerMap.has(h.toLowerCase())
  );
  if (missingHeaders.length > 0) {
    return {
      rows: [],
      errors: [{ row: 0, column: "", value: "", message: `Missing columns: ${missingHeaders.join(", ")}` }],
    };
  }

  const productLookup = new Map(
    products.map((p) => [`${p.productType}-${p.id}-${p.providerId ?? ""}`, p])
  );

  const rows: CsvRow[] = [];
  const errors: CsvValidationError[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const rowNum = i + 1;

    const rawProductType = (cells[headerMap.get("product_type")!] ?? "").trim().toLowerCase();
    const productId = (cells[headerMap.get("product_id")!] ?? "").trim();
    const rawProviderId = (cells[headerMap.get("provider_id")!] ?? "").trim();
    const title = restoreSpreadsheetText(cells[headerMap.get("title")!] ?? "");

    const productType = rawProductType === "basket" || rawProductType === "b"
      ? "basket"
      : rawProductType === "portfolio" || rawProductType === "p"
      ? "portfolio"
      : rawProductType === "managedstrategy" ||
        rawProductType === "managed_strategy" ||
        rawProductType === "managed strategy" ||
        rawProductType === "m"
      ? "managedStrategy"
      : null;

    if (!productType) {
      errors.push({ row: rowNum, column: "product_type", value: rawProductType, message: `Invalid product_type "${rawProductType}". Must be "basket", "portfolio", or "managedStrategy"` });
      continue;
    }

    const providerId = productType === "managedStrategy" ? null : rawProviderId;
    const lookupKey = `${productType}-${productId}-${providerId ?? ""}`;
    if (!productLookup.has(lookupKey)) {
      errors.push({ row: rowNum, column: "product_id", value: productId, message: `Product not found: ${productType} #${productId} (provider ${providerId ?? "none"})` });
      continue;
    }

    const tags: Partial<ProductMetadata> = {};
    for (const tagKey of FIELD_KEYS) {
      const colIdx = headerMap.get(tagKey.toLowerCase());
      if (colIdx === undefined) continue;
      const cellValue = (cells[colIdx] ?? "").trim();
      if (cellValue === "" || cellValue === "--") continue;

      if (TEXT_KEYS.has(tagKey)) {
        (tags as Record<string, string>)[tagKey] = restoreSpreadsheetText(cellValue);
        continue;
      }

      const validOptions = OPTIONS_MAP.get(tagKey);
      if (!validOptions) continue;
      const canonical = normalizeToCanonical(cellValue, validOptions);
      if (canonical === null) {
        errors.push({
          row: rowNum,
          column: tagKey,
          value: cellValue,
          message: `Invalid value "${cellValue}". Allowed: ${validOptions.join(", ")}`,
        });
      } else {
        (tags as any)[tagKey] = canonical;
      }
    }

    rows.push({ product_type: productType, product_id: productId, provider_id: providerId, title, tags });
  }

  return { rows, errors };
}

/**
 * Applies the CSV as a full replacement for configured fields while retaining
 * metadata keys that the CSV does not manage.
 */
export function mergeCsvMetadata(
  currentMetadata: ProductMetadata | null,
  csvTags: Partial<ProductMetadata>
): ProductMetadata | null {
  const merged = { ...(currentMetadata ?? {}) } as Record<string, unknown>;

  for (const key of FIELD_KEYS) {
    delete merged[key];
  }
  Object.assign(merged, csvTags);

  return Object.keys(merged).length > 0 ? (merged as ProductMetadata) : null;
}
